import { Badge, Button, Card, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Eye, Globe, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { createPage, getPage, publishPage, unpublishPage, updatePage } from '../../lib/content-api'
import type { BodyFormat, ContentStatus } from '../../lib/content-api'
import { ContentBody } from './markdown'
import { Field, Select, Textarea, slugify, statusBadgeVariant } from './fields'

interface FormState {
  title: string
  slug: string
  bodyFormat: BodyFormat
  body: string
  excerpt: string
  seoDescription: string
}

const EMPTY: FormState = {
  title: '',
  slug: '',
  bodyFormat: 'markdown',
  body: '',
  excerpt: '',
  seoDescription: '',
}

export function PageEditor() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [slugTouched, setSlugTouched] = useState(false)
  const [status, setStatus] = useState<ContentStatus>('draft')
  const [showPreview, setShowPreview] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const detail = useQuery({
    queryKey: ['content', 'pages', id],
    queryFn: () => getPage(id!),
    enabled: !isNew,
  })

  useEffect(() => {
    if (detail.data) {
      const p = detail.data
      setForm({
        title: p.title,
        slug: p.slug,
        bodyFormat: p.bodyFormat,
        body: p.body,
        excerpt: p.excerpt ?? '',
        seoDescription: p.seoDescription ?? '',
      })
      setStatus(p.status)
      setSlugTouched(true)
    }
  }, [detail.data])

  const payload = () => ({
    title: form.title.trim(),
    slug: form.slug.trim(),
    bodyFormat: form.bodyFormat,
    body: form.body,
    excerpt: form.excerpt.trim() || null,
    seoDescription: form.seoDescription.trim() || null,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['content', 'pages'] })

  const saveMut = useMutation({
    mutationFn: async () => {
      if (isNew) return createPage(payload())
      return updatePage(id!, payload())
    },
    onSuccess: (saved) => {
      setFormError(null)
      invalidate()
      if (isNew) navigate(`/app/content/pages/${saved.id}`, { replace: true })
    },
    onError: (e) => setFormError(e instanceof Error ? e.message : 'Could not save page.'),
  })

  const publishMut = useMutation({
    mutationFn: () => (status === 'published' ? unpublishPage(id!) : publishPage(id!)),
    onSuccess: (saved) => {
      setStatus(saved.status)
      invalidate()
    },
  })

  const canSave = form.title.trim().length > 0 && form.slug.trim().length > 0

  if (!isNew && detail.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-muted">
        <Spinner /> Loading page…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted">
        <Link to="/app/content/pages" className="inline-flex items-center gap-1 hover:text-fg">
          <ArrowLeft className="size-4" /> Pages
        </Link>
      </div>

      <PageHeader
        title={isNew ? 'New page' : form.title || 'Edit page'}
        description={isNew ? 'Draft a new public page.' : 'Edit page content and metadata.'}
        actions={
          <>
            {!isNew && (
              <Badge variant={statusBadgeVariant(status)} className="capitalize">
                {status}
              </Badge>
            )}
            <Button variant="secondary" onClick={() => setShowPreview((v) => !v)}>
              <Eye />
              {showPreview ? 'Hide preview' : 'Preview'}
            </Button>
            {!isNew && (
              <Button
                variant="outline"
                onClick={() => publishMut.mutate()}
                disabled={publishMut.isPending}
              >
                <Globe />
                {status === 'published' ? 'Unpublish' : 'Publish'}
              </Button>
            )}
            <Button onClick={() => saveMut.mutate()} disabled={!canSave || saveMut.isPending}>
              {saveMut.isPending ? <Spinner /> : <Save />}
              Save
            </Button>
          </>
        }
      />

      {formError && (
        <div className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
          {formError}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr,340px]">
        <Card className="space-y-5 p-5">
          <Field label="Title" htmlFor="page-title">
            <Input
              id="page-title"
              value={form.title}
              placeholder="About our church"
              onChange={(e) => {
                set('title', e.target.value)
                if (isNew && !slugTouched) set('slug', slugify(e.target.value))
              }}
            />
          </Field>

          <Field label="Slug" htmlFor="page-slug" hint="Lowercase, hyphenated. Unique per site.">
            <Input
              id="page-slug"
              value={form.slug}
              placeholder="about-our-church"
              onChange={(e) => {
                setSlugTouched(true)
                set('slug', slugify(e.target.value))
              }}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Body format" htmlFor="page-format">
              <Select
                id="page-format"
                value={form.bodyFormat}
                onChange={(v) => set('bodyFormat', v as BodyFormat)}
              >
                <option value="markdown">Markdown</option>
                <option value="plaintext">Plain text</option>
              </Select>
            </Field>
          </div>

          <Field
            label="Body"
            htmlFor="page-body"
            hint={
              form.bodyFormat === 'markdown'
                ? 'Supports # headings, **bold**, *italic*, lists, links.'
                : undefined
            }
          >
            <Textarea
              id="page-body"
              value={form.body}
              onChange={(e) => set('body', e.target.value)}
              className="min-h-[20rem] font-mono"
              placeholder="Write your page content here…"
            />
          </Field>
        </Card>

        <div className="space-y-6">
          <Card className="space-y-5 p-5">
            <Label>Metadata</Label>
            <Field label="Excerpt" htmlFor="page-excerpt" hint="Short summary for listings.">
              <Textarea
                id="page-excerpt"
                value={form.excerpt}
                onChange={(e) => set('excerpt', e.target.value)}
                className="min-h-20"
              />
            </Field>
            <Field label="SEO description" htmlFor="page-seo">
              <Textarea
                id="page-seo"
                value={form.seoDescription}
                onChange={(e) => set('seoDescription', e.target.value)}
                className="min-h-20"
              />
            </Field>
          </Card>

          {showPreview && (
            <Card className="space-y-3 p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-dim">Preview</div>
              <h2 className="font-display text-xl font-bold">{form.title || 'Untitled'}</h2>
              {form.body ? (
                <ContentBody format={form.bodyFormat} body={form.body} />
              ) : (
                <p className="text-sm text-dim">Nothing to preview yet.</p>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
